const BASE_URL = 'https://tu-api.railway.app/api'

function getToken(): string | null {
  return localStorage.getItem('vimutti_admin_token')
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  const token = getToken()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const res = await fetch(`${BASE_URL}${endpoint}`, {
    ...options,
    headers,
  })

  if (res.status === 401) {
    localStorage.removeItem('vimutti_admin_token')
    window.location.href = '/admin/login'
    throw new Error('No autorizado')
  }

  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: 'Error desconocido' }))
    throw new Error(error.error || `HTTP ${res.status}`)
  }

  return res.json()
}

// ── Public API (no auth required) ──

export const publicApi = {
  getSessions(params?: { month?: number; year?: number; modality?: string }) {
    const query = new URLSearchParams()
    if (params?.month) query.set('month', String(params.month))
    if (params?.year) query.set('year', String(params.year))
    if (params?.modality) query.set('modality', params.modality)
    return request<{ sessions: any[] }>(`/sessions?${query}`)
  },

  getSession(id: string) {
    return request<{ session: any }>(`/sessions/${id}`)
  },

  createPaymentIntent(data: {
    sessionId: string
    studentName: string
    studentEmail: string
    studentPhone?: string
    level?: string
  }) {
    return request<{
      clientSecret: string
      bookingId: string
      amount: number
      className: string
      sessionDate: string
      location: string
    }>('/bookings/create-payment-intent', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  cancelBooking(bookingId: string) {
    return request<{ success: boolean }>(`/bookings/cancel/${bookingId}`, {
      method: 'POST',
    })
  },
}

// ── Admin API (auth required) ──

export const adminApi = {
  // Auth
  login(email: string, password: string) {
    return request<{
      token: string
      admin: { id: string; email: string; name: string }
    }>('/admin/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
  },

  // Dashboard
  getDashboard() {
    return request<{
      totalBookingsThisWeek: number
      revenueThisWeek: number
      totalBookingsThisMonth: number
      revenueThisMonth: number
      upcomingSessions: number
      totalStudents: number
      occupancyRate: number
      recentBookings: any[]
      weekSessions: any[]
    }>('/admin/dashboard')
  },

  // Sessions
  getSessions(params?: { week?: string; status?: string; modality?: string }) {
    const query = new URLSearchParams()
    if (params?.week) query.set('week', params.week)
    if (params?.status) query.set('status', params.status)
    if (params?.modality) query.set('modality', params.modality)
    return request<{ sessions: any[] }>(`/admin/sessions?${query}`)
  },

  createSession(data: {
    class_type_id: string
    location_id?: string
    session_date: string
    start_time: string
    capacity: number
    zoom_link?: string
    notes?: string
  }) {
    return request<{ session: any }>('/admin/sessions', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  updateSession(id: string, data: any) {
    return request<{ session: any }>(`/admin/sessions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  rescheduleSession(id: string, session_date: string, start_time: string) {
    return request<{ session: any }>(`/admin/sessions/${id}/reschedule`, {
      method: 'PATCH',
      body: JSON.stringify({ session_date, start_time }),
    })
  },

  cancelSession(id: string) {
    return request<{ success: boolean }>(`/admin/sessions/${id}`, {
      method: 'DELETE',
    })
  },

  // Bookings
  getBookings(params?: { status?: string }) {
    const query = new URLSearchParams()
    if (params?.status) query.set('status', params.status)
    return request<{ bookings: any[] }>(`/admin/bookings?${query}`)
  },

  updateBookingStatus(id: string, booking_status: string) {
    return request<{ booking: any }>(`/admin/bookings/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ booking_status }),
    })
  },

  refundBooking(id: string) {
    return request<{ success: boolean }>(`/admin/bookings/${id}/refund`, {
      method: 'POST',
    })
  },

  // Locations
  getLocations() {
    return request<{ locations: any[] }>('/admin/locations')
  },

  createLocation(data: any) {
    return request<{ location: any }>('/admin/locations', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  updateLocation(id: string, data: any) {
    return request<{ location: any }>(`/admin/locations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  // Class Types
  getClassTypes() {
    return request<{ classTypes: any[] }>('/admin/class-types')
  },

  createClassType(data: any) {
    return request<{ classType: any }>('/admin/class-types', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  updateClassType(id: string, data: any) {
    return request<{ classType: any }>(`/admin/class-types/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },
}
